/**
 * Reading anchors: where the reader is, in terms that survive a re-layout.
 *
 * The navigator keeps the reading position as a progression fraction of the
 * chapter file, which is what drifts: in a 40-page chapter a rounding of the
 * fraction is a page or more, and every reflow compounds it. An anchor names
 * the first visible element by a selector and quotes its first visible words.
 * The words do not move when the layout does. Nothing here touches the
 * navigator, so the viewer alone decides when to take an anchor
 * and when to return to it.
 *
 * A reading `context` is `{ scroll, edgeStart }`: the reading axis, and where
 * the reader's view starts along it (below the header in scroll mode, past
 * the gutter in columns), as the viewer's `_readingContext()` reports it.
 */
import { Locator, LocatorLocations, LocatorText } from '@readium/shared';
import { getCssSelector } from 'css-selector-generator';

// An anchor quotes this many words of the first visible line, with this much
// of the text before them to tell a repeated phrase apart.
const READING_ANCHOR_WORDS = 6;
const READING_ANCHOR_CONTEXT_CHARS = 40;

export const GO_TEXT = 'go_text';

/**
 * Tolerance in painted pixels for the reading-edge tests, so a box sitting
 * exactly at an edge (or off by a subpixel rounding) still counts.
 */
export const READING_EDGE_EPSILON = 2;

/**
 * @returns {boolean} whether the box starts the screen the reader is on
 * rather than the next one: inside the column for paginated, at or above the
 * reading line for scroll. This is the rule PagebreakTracker decides a
 * screen's printed page by, so a landing and the readout agree on what
 * "this page" means.
 */
export function beginsOnReadingScreen(wnd, rect, { scroll, edgeStart }) {
    return scroll
        ? rect.top <= edgeStart + READING_EDGE_EPSILON
        : rect.left < wnd.innerWidth - edgeStart - READING_EDGE_EPSILON;
}

/**
 * @returns {boolean} whether the box intersects the viewport along the reading
 * axis: horizontal for columns, vertical for scroll, matching how Readium
 * decides what is "on the current page". `edgeStart` insets the leading side.
 */
export function intersectsReadingViewport(wnd, rect, { scroll, edgeStart }) {
    return scroll
        ? rect.bottom > edgeStart && rect.top < wnd.innerHeight
        : rect.right > edgeStart && rect.left < wnd.innerWidth;
}

/** @returns {boolean} whether the element has a text node of its own with something in it. */
function hasOwnText(el) {
    for (const node of el.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && /\S/.test(node.nodeValue)) return true;
    }
    return false;
}

/** @returns {boolean} whether the element is display:none / visibility:hidden. */
export function isElementHidden(wnd, el) {
    const style = wnd.getComputedStyle(el);
    return !style || style.display === 'none' || style.visibility === 'hidden';
}

/**
 * Finds the first element (in reading order) visible at the leading edge of the
 * current viewport, mirroring Readium's own first-visible walk. Recurses through
 * visible containers to reach the precise leading descendant, so the returned
 * element marks the start of what the reader currently sees.
 * @param {Window} wnd - the resource iframe's window
 * @param {Element} root - element to search within (starts at document.body)
 * @param {{ scroll: boolean, edgeStart: number }} context - the reading context
 * @param {object} [options]
 * @param {boolean} [options.requireExtent] - skip zero-extent markers (InDesign's
 *   empty `<a id>`), which mark a place but are not a reading position. The
 *   table of contents keeps them, since they are what its entries point at.
 * @param {boolean} [options.stopAtText] - treat an element with text of its own
 *   as the leaf, so "first words <em>later words</em>" quotes the first words.
 * @returns {Element} the first visible element, or `root` when none of its children qualify
 */
export function findFirstVisibleElement(wnd, root, context, options = {}) {
    const { requireExtent = false, stopAtText = false } = options;
    if (stopAtText && root !== wnd.document.body && hasOwnText(root)) return root;
    for (const child of root.children) {
        const rect = child.getBoundingClientRect();
        if (requireExtent && !(context.scroll ? rect.height : rect.width)) continue;
        if (!intersectsReadingViewport(wnd, rect, context) || isElementHidden(wnd, child)) continue;
        return findFirstVisibleElement(wnd, child, context, options);
    }
    return root;
}

/**
 * Document-rooted selector (no `root` option) so Readium's go_text, which does
 * `document.querySelector(cssSelector)`, resolves it uniquely.
 * @returns {string|null}
 */
function cssSelectorFor(element) {
    return getCssSelector(element, { selectors: ['id', 'class', 'tag', 'nthoftype', 'nthchild', 'attribute'] }) || null;
}

/**
 * Quotes the first words visible inside `element`: what the reader sees first,
 * with the text before them for disambiguation. A paragraph that began in the
 * previous column has its start off-screen, so an anchor on the element alone
 * would land a page early. The visible words land on the line the reader was on.
 * @returns {{ highlight: string, before: string }|null} null when the element shows no text
 */
function findFirstVisibleText(wnd, element, context) {
    const doc = wnd.document;
    const range = doc.createRange();
    const walker = doc.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let before = '';
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const text = node.nodeValue;
        const wordPattern = /\S+/g;
        let start = -1;
        let end = -1;
        let count = 0;
        let match;
        while (count < READING_ANCHOR_WORDS && (match = wordPattern.exec(text))) {
            range.setStart(node, match.index);
            range.setEnd(node, match.index + match[0].length);
            const rect = range.getBoundingClientRect();
            if (!(rect.width > 0 && intersectsReadingViewport(wnd, rect, context))) {
                if (start >= 0) break; // the visible run ended at the column edge
                continue;
            }
            if (start < 0) start = match.index;
            end = match.index + match[0].length;
            count++;
        }
        if (start >= 0) {
            return {
                highlight: text.slice(start, end),
                before: (before + text.slice(0, start)).slice(-READING_ANCHOR_CONTEXT_CHARS),
            };
        }
        before = (before + text).slice(-READING_ANCHOR_CONTEXT_CHARS);
    }
    return null;
}

/**
 * Finds a quoted text inside `element` again, as a Range: the exact text with
 * its preceding context first, the text alone when the context no longer matches.
 * @returns {Range|null}
 */
function rangeOfText(doc, element, { highlight, before = '' }) {
    const content = element.textContent;
    const withContext = before ? content.indexOf(before + highlight) : -1;
    const start = withContext >= 0 ? withContext + before.length : content.indexOf(highlight);
    if (start < 0) return null;
    const end = start + highlight.length;
    const range = doc.createRange();
    const walker = doc.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let offset = 0;
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const next = offset + node.nodeValue.length;
        if (start >= offset && start < next) range.setStart(node, start - offset);
        if (end > offset && end <= next) {
            range.setEnd(node, end - offset);
            return range;
        }
        offset = next;
    }
    return null;
}

/**
 * Takes an anchor for what the reader sees now: `location` (the navigator's
 * current locator, kept as its last resort when neither the words nor the
 * selector resolve any more) plus a selector for the first visible element
 * and a quote of its first visible words.
 * @param {Window|null} wnd - the chapter frame's window
 * @param {Locator|undefined} location - the navigator's current location
 * @param {{ scroll: boolean, edgeStart: number }} context - the reading context
 * @returns {Locator|null} null before the frame exists or when nothing is visible
 */
export function captureReadingAnchor(wnd, location, context) {
    const body = wnd?.document?.body;
    if (!body || !location) return null;

    const element = findFirstVisibleElement(wnd, body, context, { requireExtent: true, stopAtText: true });
    if (element === body) return null;
    const cssSelector = cssSelectorFor(element);
    if (!cssSelector) return null;

    const text = findFirstVisibleText(wnd, element, context);
    return new Locator({
        href: location.href,
        type: location.type,
        title: location.title,
        locations: new LocatorLocations({
            ...location.locations,
            otherLocations: new Map([['cssSelector', cssSelector]]),
        }),
        text: text ? new LocatorText(text) : undefined,
    });
}

function anchorSelector(anchor) {
    return anchor?.locations?.otherLocations?.get('cssSelector') ?? null;
}

/**
 * The `go_text` payload for an anchor: the quoted words and the selector
 * that scopes them.
 * @returns {[object|string, string]|null} null when the anchor names no element
 */
export function readingAnchorPayload(anchor) {
    const cssSelector = anchorSelector(anchor);
    if (!cssSelector) return null;
    return [anchor.text?.highlight ? anchor.text.serialize() : '', cssSelector];
}

/**
 * Finds an anchor in a frame document: the quoted words as a Range, or the
 * element when there were none.
 * @returns {Range|Element|null} null when the anchor is gone from the document
 */
export function locateReadingAnchor(doc, anchor) {
    const cssSelector = anchorSelector(anchor);
    const element = cssSelector ? doc.querySelector(cssSelector) : null;
    if (!element) return null;
    return (anchor.text?.highlight && rangeOfText(doc, element, anchor.text)) || element;
}

/**
 * Scrolls a located anchor to the reading edge, below the header. Readium's
 * own scroll-mode landing centres the target in the viewport instead.
 * @param {Window} wnd - the chapter frame's window
 * @param {Range|Element} target - from locateReadingAnchor
 * @param {number} edgeStart - where the view starts, see the reading context
 */
/**
 * The first non-blank character after `marker`, as a Range so the callers
 * above can measure or scroll to it. A pagebreak marker is empty and sits
 * between two words, so where a printed page starts is the text that
 * follows it, not the marker's own box.
 *
 * @param {Element} marker
 * @returns {Range|null} null when nothing but whitespace follows
 */
export function firstTextRangeAfter(marker) {
    const doc = marker.ownerDocument;
    const walker = doc.createTreeWalker(doc.body ?? doc.documentElement, NodeFilter.SHOW_TEXT);
    walker.currentNode = marker;

    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (marker.contains(node)) continue;
        const offset = node.nodeValue.search(/\S/);
        if (offset === -1) continue;

        const range = doc.createRange();
        range.setStart(node, offset);
        range.setEnd(node, offset + 1);
        return range;
    }

    return null;
}

export function scrollToReadingAnchor(wnd, target, edgeStart) {
    wnd.scrollTo(0, Math.max(0, target.getBoundingClientRect().top + wnd.scrollY - edgeStart));
}
